import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link as ScrollLink } from 'react-scroll';
import imageCompression from 'browser-image-compression';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.NODE_ENV === 'production' 
  ? '' // Bruker en relativ sti i produksjon
  : 'http://localhost:5000'; // Beholder den gamle for lokal utvikling

// Hjelperfunksjoner
const getReadableTitle = (item) => {
  const pageMap = {
    hjem: "Hjemmeside",
    naprapati: "Hva er naprapati?",
    'om-oss': "Om Oss",
    kontakt: "Kontakt",
    bedrift: "Bedrift",
    global: "Global (Footer)",
  };
  const elementMap = {
    hero_tittel: "Hovedtittel",
    hero_undertittel: "Undertittel",
    feature1_bilde: "Bilde, Førstegangskonsultasjon",
    feature2_bilde: "Bilde, Tilpasset behandling",
    naprapati_video_bilde: "Bilde, Videokonsultasjon",
    om_oss_bilde: "Bilde, Om Oss Portrett",
  };
  const pageName = pageMap[item.page] || item.page;
  const elementName = elementMap[item.element_id] || item.element_id.replace(/_/g, ' ');
  return `${pageName} | ${elementName}`;
};

const slugify = (text) => {
  return text.toString().toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

const UndoButton = ({ closeToast, onUndo }) => (
  <button className="toast-undo-button" onClick={() => { onUndo(); closeToast(); }}>
    Angre
  </button>
);

const createUndoToast = (originalStateSetter, apiEndpoint, successMessage, failureMessage) => {
  let timerId = null;
  const performDelete = () => {
    axios.delete(apiEndpoint)
      .then(() => toast.success(successMessage))
      .catch((err) => {
        toast.error(err.response?.data?.error || failureMessage);
        originalStateSetter();
      });
  };
  const handleUndo = () => {
    clearTimeout(timerId);
    originalStateSetter();
    toast.info("Sletting angret.");
  };
  timerId = setTimeout(performDelete, 5000);
  toast.warn(({ closeToast }) => (
    <div>
      Sletter om 5 sekunder...
      <UndoButton closeToast={closeToast} onUndo={handleUndo} />
    </div>
  ), { autoClose: 5000, onClose: () => clearTimeout(timerId) });
};

function AdminDashboard() {
  const [content, setContent] = useState([]);
  const [prices, setPrices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newPriceInputs, setNewPriceInputs] = useState({});
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingCondition, setEditingCondition] = useState(null);
  const [openGroup, setOpenGroup] = useState(null); // Ny state for å holde styr på åpen gruppe
  const [testimonials, setTestimonials] = useState([]);
  const [editingTestimonial, setEditingTestimonial] = useState(null);

  // --- NY LOGIKK FOR UTLOGGING ---
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('authToken'); // Fjerner "adgangskortet"
    navigate('/admin'); // Sender brukeren til innloggingssiden
    toast.info("Du er nå logget ut.");
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [contentRes, pricesRes, categoriesRes, articlesRes, conditionsRes, testimonialsRes] = await Promise.all([
          axios.get(`${API_URL}/api/content/all`),
          axios.get(`${API_URL}/api/prices/all`),
          axios.get(`${API_URL}/api/categories`),
          axios.get(`${API_URL}/api/articles`),
          axios.get(`${API_URL}/api/conditions/all`),
          axios.get(`${API_URL}/api/testimonials`)
        ]);
        const navOrder = ['hjem', 'naprapati', 'om-oss', 'kontakt', 'bedrift', 'global'];
        const sortedContent = contentRes.data.sort((a, b) => {
          const indexA = navOrder.indexOf(a.page);
          const indexB = navOrder.indexOf(b.page);
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        });
        setContent(sortedContent);
        setPrices(pricesRes.data);
        setCategories(categoriesRes.data);
        setArticles(articlesRes.data);
        setConditions(conditionsRes.data);
        setTestimonials(testimonialsRes.data);
        setLoading(false);
      } catch (err) {
        console.error("Feil ved henting av data:", err);
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

// --- Håndteringsfunksjoner ---
const handleContentChange = (id, newValue) => {
  setContent(content.map(item =>
    item.id === id ? { ...item, value: newValue } : item
  ));
};

const handleSaveContent = (id, valueToSave) => {
  axios.put(`${API_URL}/api/content/${id}`, { value: valueToSave })
    .then(() => toast.success("Elementet er lagret!"))
    .catch(() => toast.error("Noe gikk galt."));
};

const handlePriceChange = (id, field, value) => {
  setPrices(prices.map(p =>
    p.id === id ? { ...p, [field]: value } : p
  ));
};

const handleSavePrice = (id) => {
  const priceToSave = prices.find(p => p.id === id);

  axios.put(`${API_URL}/api/prices/${id}`, priceToSave)
    .then(() => toast.success("Pris lagret!"))
    .catch(() => toast.error("Feil ved lagring."));
};

const handleNewPriceInputChange = (categoryId, field, value) => {
  setNewPriceInputs({
    ...newPriceInputs,
    [categoryId]: {
      ...newPriceInputs[categoryId],
      [field]: value
    }
  });
};

const handleAddNewPrice = (e, categoryId) => {
  e.preventDefault();

  const newPriceData = {
    category_id: categoryId,
    service_name: newPriceInputs[categoryId]?.service_name || "",
    price: newPriceInputs[categoryId]?.price || "",
    details: newPriceInputs[categoryId]?.details || ""
  };

  axios.post(`${API_URL}/api/prices`, newPriceData)
    .then(() => {
      axios.get(`${API_URL}/api/prices/all`)
        .then(res => setPrices(res.data));

      setNewPriceInputs({
        ...newPriceInputs,
        [categoryId]: {}
      });

      toast.success("Ny pris lagt til!");
    })
    .catch(() => toast.error("Feil."));
};

const handleAddCategory = (e) => {
  e.preventDefault();

  axios.post(`${API_URL}/api/categories`, { name: newCategoryName })
    .then(res => {
      setCategories([
        ...categories,
        {
          id: res.data.newId,
          name: res.data.name,
          sort_order: res.data.sort_order
        }
      ]);
      setNewCategoryName("");
      toast.success("Ny kategori opprettet!");
    })
    .catch(err => toast.error(err.response.data.error || "Feil"));
};

const handleCategoryMove = (index, direction) => {
  const newCategories = [...categories];
  const item = newCategories.splice(index, 1)[0];
  const newIndex = direction === "up" ? index - 1 : index + 1;

  newCategories.splice(newIndex, 0, item);
  setCategories(newCategories);

  const orderedIds = newCategories.map(c => c.id);
  axios.post(`${API_URL}/api/categories/reorder`, { orderedIds });
};

const handleImageUpload = async (contentId, file) => {
  if (!file) return;

  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true
  };

  try {
    const compressedFile = await imageCompression(file, options);
    const formData = new FormData();
    formData.append("image", compressedFile, compressedFile.name);

    const uploadRes = await axios.post(`${API_URL}/api/upload/image`, formData);
    const { imageUrl } = uploadRes.data;

    await axios.put(`${API_URL}/api/content/${contentId}`, { value: imageUrl });

    setContent(content.map(c =>
      c.id === contentId ? { ...c, value: imageUrl } : c
    ));

    toast.success("Bilde lagret!");
  } catch (err) {
    toast.error("Feil ved opplasting.");
  }
};

const handleEditArticle = (article) => {
  setEditingArticle({
    ...article,
    image_urls: JSON.parse(article.image_urls || "[]")
  });
};

const handleNewArticle = () => {
  setEditingArticle({
    title: "",
    slug: "",
    intro_text: "",
    full_text: "",
    image_urls: [],
    layout_option: "images_before",
    gallery_size: "medium"
  });
};

const handleArticleChange = (field, value) => {
  let newSlug = editingArticle.slug;

  if (field === "title") {
    newSlug = slugify(value);
  }

  setEditingArticle({
    ...editingArticle,
    [field]: value,
    slug: newSlug
  });
};

const handleArticleImageUpload = async (files) => {
  if (!files || files.length === 0) return;
  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  };
  try {
    const compressedFiles = await Promise.all(
      [...files].map(file => imageCompression(file, options))
    );
    const formData = new FormData();
    compressedFiles.forEach(file => {
      formData.append("images", file, file.name);
    });
    const res = await axios.post(`${API_URL}/api/upload/gallery`, formData);
    
    // Oppdaterer KUN den lokale tilstanden for forhåndsvisning
    setEditingArticle({
      ...editingArticle,
      image_urls: [...editingArticle.image_urls, ...res.data.imageUrls]
    });
    
    toast.info('Nye bilder er klare. Husk å trykke "Lagre artikkel" for å bekrefte.');
  } catch (err) {
    toast.error("Feil ved opplasting av bilder.");
  }
};

const handleSaveArticle = async () => {
  const articleData = {
    ...editingArticle,
    image_urls: JSON.stringify(editingArticle.image_urls)
  };

  try {
    if (editingArticle.id) {
      await axios.put(`${API_URL}/api/articles/${editingArticle.id}`, articleData);
    } else {
      await axios.post(`${API_URL}/api/articles`, articleData);
    }

    toast.success("Artikkel lagret!");
    setEditingArticle(null);

    axios.get(`${API_URL}/api/articles`)
      .then(res => setArticles(res.data));
  } catch (err) {
    toast.error("Feil ved lagring.");
  }
};

const handleNewCondition = () => {
  setEditingCondition({
    name: "",
    slug: "",
    image_url: "/images/placeholder.png",
    short_description: "",
    long_description: ""
  });
};

const handleEditCondition = (condition) => {
  setEditingCondition(condition);
};

const handleConditionChange = (field, value) => {
  let newSlug = editingCondition.slug;

  if (field === "name") {
    newSlug = slugify(value);
  }

  setEditingCondition({
    ...editingCondition,
    [field]: value,
    slug: newSlug
  });
};

const handleConditionImageUpload = async (file) => {
  if (!file) return;
  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
  };
  try {
    const compressedFile = await imageCompression(file, options);
    const formData = new FormData();
    formData.append("image", compressedFile, compressedFile.name);
    
    // Laster kun opp bildet og får tilbake URL-en
    const uploadRes = await axios.post(`${API_URL}/api/upload/image`, formData);
    const { imageUrl } = uploadRes.data;
    
    // Oppdaterer KUN den lokale tilstanden for forhåndsvisning
    setEditingCondition({ ...editingCondition, image_url: imageUrl });
    
    toast.info('Nytt bilde er klart. Husk å trykke "Lagre" for å bekrefte alle endringer.');
  } catch (err) {
    toast.error("Feil ved opplasting av bilde.");
  }
};

const handleSaveCondition = async () => {
  try {
    if (editingCondition.id) {
      await axios.put(`${API_URL}/api/conditions/${editingCondition.id}`, editingCondition);
    } else {
      await axios.post(`${API_URL}/api/conditions`, editingCondition);
    }

    toast.success("Tilstand lagret!");
    setEditingCondition(null);

    axios.get(`${API_URL}/api/conditions/all`)
      .then(res => setConditions(res.data));
  } catch (err) {
    toast.error("Feil ved lagring.");
  }
};

const handleConditionMove = (index, direction) => {
  const newConditions = [...conditions];
  const item = newConditions.splice(index, 1)[0];
  const newIndex = direction === "up" ? index - 1 : index + 1;

  newConditions.splice(newIndex, 0, item);
  setConditions(newConditions);

  const orderedIds = newConditions.map(c => c.id);
  axios.post(`${API_URL}/api/conditions/reorder`, { orderedIds });
};

// --- FUNKSJONER FOR HJEMMESIDE-KARUSELL ---

const handleHomepageCarouselUpload = async (files) => {
  if (!files || files.length === 0) return;
  const options = { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true };
  try {
    const compressedFiles = await Promise.all([...files].map(file => imageCompression(file, options)));
    const formData = new FormData();
    compressedFiles.forEach(file => formData.append('images', file, file.name));
    const res = await axios.post(`${API_URL}/api/upload/gallery`, formData);
    
    const carouselItem = content.find(item => item.element_id === 'hjem_carousel_images');
    const currentImages = JSON.parse(carouselItem.value || '[]');
    const newImageUrls = [...currentImages, ...res.data.imageUrls];
    
    handleContentChange(carouselItem.id, JSON.stringify(newImageUrls));
    toast.info('Bilder lagt til. Trykk "Lagre karusell" for å bekrefte.');
  } catch (err) {
    toast.error('Feil ved opplasting av karusellbilder.');
  }
};

const handleRemoveHomepageCarouselImage = (urlToRemove) => {
  const carouselItem = content.find(item => item.element_id === 'hjem_carousel_images');
  const originalImages = JSON.parse(carouselItem.value || '[]');
  const newImageUrls = originalImages.filter(url => url !== urlToRemove);
  
  handleContentChange(carouselItem.id, JSON.stringify(newImageUrls));

  toast.warn(({ closeToast }) => (
    <div>
      Bildet er fjernet.
      <UndoButton closeToast={closeToast} onUndo={() => {
        handleContentChange(carouselItem.id, JSON.stringify(originalImages));
        toast.info("Fjerning av bilde angret.");
      }} />
    </div>
  ), { autoClose: 5000 });
};

const carouselImagesContent = content.find(item => item.element_id === 'hjem_carousel_images');

// --- OPPDATERTE SLETT-FUNKSJONER ---
const handleDeletePrice = (id) => {
  const originalPrices = [...prices];
  setPrices(prices.filter(p => p.id !== id));

  createUndoToast(
    () => setPrices(originalPrices),
    `${API_URL}/api/prices/${id}`,
    "Pris slettet permanent.",
    "Feil ved sletting av pris."
  );
};

const handleDeleteCategory = (id) => {
  const originalCategories = [...categories];
  setCategories(categories.filter(c => c.id !== id));

  createUndoToast(
    () => setCategories(originalCategories),
    `${API_URL}/api/categories/${id}`,
    "Kategori slettet permanent.",
    "Feil: Kategorien har kanskje priser knyttet til seg."
  );
};

const handleDeleteArticle = (id) => {
  const originalArticles = [...articles];
  setArticles(articles.filter(a => a.id !== id));

  createUndoToast(
    () => setArticles(originalArticles),
    `${API_URL}/api/articles/${id}`,
    "Artikkel slettet permanent.",
    "Feil ved sletting av artikkel."
  );
};

const handleDeleteCondition = (id) => {
  const originalConditions = [...conditions];
  setConditions(conditions.filter(c => c.id !== id));

  createUndoToast(
    () => setConditions(originalConditions),
    `${API_URL}/api/conditions/${id}`,
    "Tilstand slettet permanent.",
    "Feil ved sletting av tilstand."
  );
};

const handleRemoveImage = (imageUrl) => {
  const originalImageUrls = [...editingArticle.image_urls];
  const newImageUrls = editingArticle.image_urls.filter(url => url !== imageUrl);

  setEditingArticle({
    ...editingArticle,
    image_urls: newImageUrls
  });

    // Viser en toast med angre-mulighet
    toast.warn(({ closeToast }) => (
      <div>
        Bildet er fjernet.
        <UndoButton 
          closeToast={closeToast} 
          onUndo={() => {
            setEditingArticle({ ...editingArticle, image_urls: originalImageUrls });
            toast.info("Fjerning av bilde angret.");
          }} 
        />
      </div>
    ), { autoClose: 5000 });
  };

const groupedContent = content.reduce((acc, item) => {
  const page = item.page;
  if (!acc[page]) {
    acc[page] = [];
  }
  acc[page].push(item);
  return acc;
}, {});

const newsActiveSwitch = content.find(item =>
  item.element_id === "hjem_nyheter_aktiv"
);

const normalHours = content.find(item =>
  item.element_id === "kontakt_apningstider_normal"
);

const hoytidHours = content.find(item =>
  item.element_id === "kontakt_apningstider_hoytid"
);

const activeStatus = content.find(item =>
  item.element_id === "kontakt_apningstider_aktiv"
);

  // --- NYE FUNKSJONER FOR TESTIMONIALS (fullstendig implementert) ---
  const handleEditTestimonial = (testimonial) => {
    setEditingTestimonial(testimonial);
  };

  const handleNewTestimonial = () => {
    setEditingTestimonial({ quote: '', author: '' });
  };
  
  const handleTestimonialChange = (field, value) => {
    setEditingTestimonial({ ...editingTestimonial, [field]: value });
  };

  const handleSaveTestimonial = async () => {
    try {
      if (editingTestimonial.id) {
        await axios.put(`${API_URL}/api/testimonials/${editingTestimonial.id}`, editingTestimonial);
      } else {
        await axios.post(`${API_URL}/api/testimonials`, editingTestimonial);
      }
      toast.success('Omtale lagret!');
      setEditingTestimonial(null);
      axios.get(`${API_URL}/api/testimonials`).then(res => setTestimonials(res.data));
    } catch (err) {
      toast.error('Feil ved lagring av omtale.');
    }
  };

  const handleDeleteTestimonial = (id) => {
    const originalTestimonials = [...testimonials];
    setTestimonials(testimonials.filter(t => t.id !== id));
    createUndoToast(() => setTestimonials(originalTestimonials), `${API_URL}/api/testimonials/${id}`, 'Omtale slettet permanent.', 'Feil ved sletting.');
  };

  const handleTestimonialMove = (index, direction) => {
    const newTestimonials = [...testimonials];
    const item = newTestimonials.splice(index, 1)[0];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    newTestimonials.splice(newIndex, 0, item);
    setTestimonials(newTestimonials);
    const orderedIds = newTestimonials.map(t => t.id);
    axios.post(`${API_URL}/api/testimonials/reorder`, { orderedIds });
  };

  if (loading) return <p>Laster inn innhold...</p>;

  return (
    <div className="admin-dashboard-layout">
      <nav className="admin-nav">
        <ul>
          <li><ScrollLink to="section-news" smooth={true} duration={500} offset={-120} activeClass="active">Nyheter</ScrollLink></li>
          <li><ScrollLink to="section-content" smooth={true} duration={500} offset={-120} activeClass="active">Tekster & Bilder</ScrollLink></li>
          <li><ScrollLink to="section-hours" smooth={true} duration={500} offset={-120} activeClass="active">Åpningstider</ScrollLink></li>
          <li><ScrollLink to="section-prices" smooth={true} duration={500} offset={-120} activeClass="active">Priser & Kat.</ScrollLink></li>
          <li><ScrollLink to="section-conditions" smooth={true} duration={500} offset={-120} activeClass="active">Plager</ScrollLink></li>
          <li><ScrollLink to="section-testimonials" smooth={true} duration={500} offset={-120} activeClass="active">Omtaler</ScrollLink></li>
        </ul>
      </nav>

      <div className="admin-dashboard-content">
        <h1>Admin Dashboard</h1>
        <button onClick={handleLogout} className="button danger-button logout-button">Logg ut</button>
        
        <section id="section-news" className="dashboard-section">
          <h2>Administrer Nyheter</h2>
          <div className="card">
            {newsActiveSwitch && (
              <div className="news-toggle-wrapper">
                <h3>Nyhetsseksjon</h3>
                <div className="toggle-container">
                  <span>Deaktivert</span>
                  <button className={`toggle-switch ${newsActiveSwitch.value === 'true' ? 'active' : ''}`} onClick={() => { const newStatus = newsActiveSwitch.value === 'true' ? 'false' : 'true'; handleContentChange(newsActiveSwitch.id, newStatus); handleSaveContent(newsActiveSwitch.id, newStatus); }}>
                    <span className="toggle-handle"></span>
                  </button>
                  <span>Aktivert</span>
                </div>
              </div>
            )}
            {editingArticle ? (
              <div className="article-editor">
                <h3>{editingArticle.id ? 'Rediger artikkel' : 'Ny artikkel'}</h3>
                <div className="form-group"><label>Tittel</label><input type="text" value={editingArticle.title} onChange={e => handleArticleChange('title', e.target.value)} /></div>
                <div className="form-group"><label>URL-slug</label><input type="text" value={editingArticle.slug} readOnly /></div>
                <div className="form-group"><label>Innledning</label><textarea value={editingArticle.intro_text} onChange={e => handleArticleChange('intro_text', e.target.value)} rows="3"></textarea></div>
                <div className="form-group"><label>Full tekst</label><ReactQuill theme="snow" value={editingArticle.full_text} onChange={(html) => handleArticleChange('full_text', html)} /></div>
                <div className="form-group"><label>Layout</label><select value={editingArticle.layout_option} onChange={e => handleArticleChange('layout_option', e.target.value)}><option value="images_before">Bilder FØR</option><option value="images_after">Bilder ETTER</option><option value="images_left">Bilder VENSTRE</option><option value="images_right">Bilder HØYRE</option></select></div>
                <div className="form-group"><label>Galleristørrelse</label><select value={editingArticle.gallery_size} onChange={e => handleArticleChange('gallery_size', e.target.value)}><option value="small">Lite</option><option value="medium">Medium</option><option value="large">Stort</option><option value="full">Full bredde</option></select></div>
                <div className="form-group">
                  <label>Bildegalleri</label>
                  <div className="image-preview-gallery">{editingArticle.image_urls.map((url, i) => (<div key={i} className="image-preview-item"><img src={url} alt=" "/><button onClick={() => handleRemoveImage(url)} className="delete-image-button">&times;</button></div>))}</div>
                  <small className="form-hint-text">Maks 5 bilder. For best resultat, prøv å bruke bilder med lignende format (enten liggende eller stående).</small>
                  <small className="form-hint-text">Bilder over 1MB komprimeres automatisk.</small>
                  <input type="file" multiple accept="image/*" onChange={e => handleArticleImageUpload(e.target.files)} />
                </div>
                <div className="button-group"><button onClick={handleSaveArticle} className="button">Lagre artikkel</button><button onClick={() => setEditingArticle(null)} className="button danger-button">Avbryt</button></div>
              </div>
            ) : (
              <div className="article-list">
                <button onClick={handleNewArticle} className="button new-article-button">Ny artikkel</button>
                {articles.map(article => (
                  <div key={article.id} className="admin-article-item">
                    <span>{article.title}</span>
                    <div className="button-group"><button onClick={() => handleEditArticle(article)} className="button small-button">Rediger</button><button onClick={() => handleDeleteArticle(article.id)} className="button danger-button small-button">Slett</button></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section id="section-content" className="dashboard-section">
          <h2>Rediger Sideinnhold & Bilder</h2>
          <div className="accordion-container-admin">
            {Object.keys(groupedContent).map(pageKey => (
              <div className="accordion-item-admin" key={pageKey}>
                <button 
                  className="accordion-header-admin" 
                  onClick={() => setOpenGroup(openGroup === pageKey ? null : pageKey)}
                >
                  <span>Rediger innhold for: <strong>{pageKey.replace('-', ' ')}</strong></span>
                  <span className={`info-button-arrow ${openGroup === pageKey ? 'open' : ''}`}>&#8250;</span>
                </button>
                <div className={`accordion-content-admin ${openGroup === pageKey ? 'open' : ''}`}>
                  <div className="accordion-content-inner-admin">
                    {/* Viser alle tekst-elementer for denne siden */}
                    {groupedContent[pageKey].filter(item => item.type === 'tekst' || item.type === 'overskrift' || item.type === 'link').map(item => (
                      <div key={item.id} className="admin-edit-item card">
                        <div className="item-info"><strong>{getReadableTitle(item).split(' | ')[1]}</strong></div>
                        <textarea value={item.value} onChange={(e) => handleContentChange(item.id, e.target.value)} rows={item.value.length > 100 ? 5 : 2} />
                        <button onClick={() => handleSaveContent(item.id, item.value)} className="button">Lagre</button>
                      </div>
                    ))}
                    
                    {/* Viser forside-karusellen KUN for 'hjem'-siden */}
                    {pageKey === 'hjem' && carouselImagesContent && (
                      <div className="card admin-edit-item">
                        <h3>Forside-karusell</h3>
                        <div className="form-group">
                          <div className="image-preview-gallery">
                          {JSON.parse(carouselImagesContent.value || '[]').map((url, i) => (
                            <div key={i} className="image-preview-item">
                              <img src={url} alt=" "/>
                              <button onClick={() => handleRemoveHomepageCarouselImage(url)} className="delete-image-button">&times;</button>
                            </div>
                          ))}
                        </div>
                          <input type="file" multiple accept="image/*" onChange={e => handleHomepageCarouselUpload(e.target.files)} />
                          <small className="form-hint-text">Maks 5 bilder. Bilder over 1MB komprimeres automatisk.</small>
                        </div>
                        <button 
                        onClick={() => {
                          const updatedCarouselItem = content.find(item => item.id === carouselImagesContent.id);
                          handleSaveContent(updatedCarouselItem.id, updatedCarouselItem.value);
                        }} 
                        className="button save-carousel-button">Lagre karusell</button>
                      </div>
                    )}

                    {/* Viser alle enkeltbilde-elementer for denne siden */}
                    {groupedContent[pageKey].filter(item => item.type === 'bilde').map(item => (
                      <div key={item.id} className="card single-image-card admin-edit-item">
                        <div className="item-info"><strong>{getReadableTitle(item)}</strong></div>
                        <img src={item.value} alt=" " className="image-preview" />
                        <label htmlFor={`upload-${item.id}`} className="button change-image-button">Bytt bilde</label>
                        <input type="file" id={`upload-${item.id}`} onChange={e => handleImageUpload(item.id, e.target.files[0])} style={{ display: 'none' }} />
                        <small className="form-hint-text">Bilder over 1MB komprimeres automatisk.</small>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {normalHours && hoytidHours && activeStatus && (
          <section id="section-hours" className="dashboard-section">
            <h2>Rediger Åpningstider</h2>

            <div className="admin-hours-section card">
              {/* Toggle-knapp for å bytte mellom vanlig og høytid */}
              <div className="hours-toggle-wrapper">
                <span>Vanlig</span>

                <button
                  className={`toggle-switch ${activeStatus.value === 'hoytid' ? 'active' : ''}`}
                  onClick={() => {
                    const newStatus = activeStatus.value === 'normal' ? 'hoytid' : 'normal';
                    handleContentChange(activeStatus.id, newStatus);
                    handleSaveContent(activeStatus.id, newStatus);
                  }}
                >
                  <span className="toggle-handle"></span>
                </button>

                <span>Høytid</span>
              </div>

              {/* Vanlige åpningstider */}
              <div className="admin-edit-item">
                <label>Vanlige tider</label>
                <textarea
                  value={normalHours.value}
                  onChange={(e) => handleContentChange(normalHours.id, e.target.value)}
                  rows={4}
                />
                <small className="form-hint-text">
                  Separer dager med komma, f.eks. "Mandag: 10-16,Tirsdag: 10-16" - Ingen mellomrom før eller etter kolon
                </small>
                <button
                  onClick={() => handleSaveContent(normalHours.id, normalHours.value)}
                  className="button"
                >
                  Lagre
                </button>
              </div>

              {/* Høytidsåpningstider */}
              <div className="admin-edit-item">
                <label>Høytidstider</label>
                <textarea
                  value={hoytidHours.value}
                  onChange={(e) => handleContentChange(hoytidHours.id, e.target.value)}
                  rows={4}
                />
                <small className="form-hint-text">
                  F.eks. "Julaften: Stengt,1. Juledag: Stengt" - Ingen mellomrom før eller etter kolon
                </small>
                <button
                  onClick={() => handleSaveContent(hoytidHours.id, hoytidHours.value)}
                  className="button"
                >
                  Lagre
                </button>
              </div>
            </div>
          </section>
        )}

        <section id="section-prices" className="dashboard-section">
          <h2>Rediger Priser & Kategorier</h2>
          {categories.map((category, index) => (
            <div key={category.id} className="category-group card">
              <div className="category-header"><div className="category-title-group"><div className="category-order-buttons"><button onClick={() => handleCategoryMove(index, 'up')} disabled={index === 0}>▲</button><button onClick={() => handleCategoryMove(index, 'down')} disabled={index === categories.length - 1}>▼</button></div><h3>{category.name}</h3></div><button onClick={() => handleDeleteCategory(category.id)} className="button danger-button small-button">Slett</button></div>
              {prices.filter(p => p.category_id === category.id).map(item => (
                <div key={item.id} className="admin-edit-item price-editor"><input type="text" value={item.service_name} onChange={e => handlePriceChange(item.id, 'service_name', e.target.value)} /><input type="text" value={item.price} onChange={e => handlePriceChange(item.id, 'price', e.target.value)} /><input type="text" value={item.details} onChange={e => handlePriceChange(item.id, 'details', e.target.value)} /><div className="button-group"><button onClick={() => handleSavePrice(item.id)} className="button">Lagre</button><button onClick={() => handleDeletePrice(item.id)} className="button danger-button">Slett</button></div></div>
              ))}
              <h4>Legg til i "{category.name}"</h4>
              <form onSubmit={(e) => handleAddNewPrice(e, category.id)} className="admin-edit-item price-editor">
              <input type="text" value={newPriceInputs[category.id]?.service_name || ''} 
              onChange={e => handleNewPriceInputChange(category.id, 'service_name', e.target.value)} placeholder="Tjenestenavn" required />
              <input type="text" value={newPriceInputs[category.id]?.price || ''} 
              onChange={e => handleNewPriceInputChange(category.id, 'price', e.target.value)} placeholder="Pris" required />
              <input type="text" value={newPriceInputs[category.id]?.details || ''} 
              onChange={e => handleNewPriceInputChange(category.id, 'details', e.target.value)} placeholder="Detaljer" />
              <button type="submit" className="button">Legg til</button></form>
              <small className="form-hint-text full-width-hint">
                  For felter uten verdi (f.eks. Detaljer), bruk en bindestrek "-".
                </small>
            </div>
          ))}
          <div className="category-group card"><h3>Ny kategori</h3><form onSubmit={handleAddCategory} className="category-add-form"><input type="text" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="Navn på ny kategori" required /><button type="submit" className="button">Opprett</button></form></div>
        </section>
        
        <section id="section-conditions" className="dashboard-section">
          <h2>Rediger Plager/Tilstander</h2>
          <div className="card">
            {editingCondition ? (
              <div className="article-editor">
                <h3>{editingCondition.id ? 'Rediger tilstand' : 'Ny tilstand'}</h3>
                <div className="form-group"><label>Navn</label><input type="text" value={editingCondition.name} onChange={e => handleConditionChange('name', e.target.value)} /></div>
                <div className="form-group"><label>URL-slug</label><input type="text" value={editingCondition.slug} readOnly /></div>
                <div className="form-group"><label>Kort beskrivelse</label><textarea value={editingCondition.short_description} onChange={e => handleConditionChange('short_description', e.target.value)} rows="3"></textarea></div>
                <div className="form-group"><label>Full beskrivelse</label><ReactQuill theme="snow" value={editingCondition.long_description} onChange={(html) => handleConditionChange('long_description', html)} /></div>
                <div className="form-group">
                  <label>Bilde</label>
                  <div className="image-preview-gallery">
                    <img src={editingCondition.image_url} alt=" " className="image-preview" />
                  </div>
                  <input type="file" accept="image/*" onChange={e => handleConditionImageUpload(e.target.files[0])} />
                  <small className="form-hint-text">Bilder over 1MB komprimeres.</small>
                </div>
                <div className="button-group">
                  <button onClick={handleSaveCondition} className="button">Lagre</button>
                  <button onClick={() => setEditingCondition(null)} className="button danger-button">Avbryt</button>
                </div>
              </div>
            ) : (
              <div className="article-list">
                <button onClick={handleNewCondition} className="button new-article-button">Legg til ny</button>
                {conditions.map((condition, index) => (
                  <div key={condition.id} className="admin-article-item">
                    <div className="category-title-group"><div className="category-order-buttons"><button onClick={() => handleConditionMove(index, 'up')} disabled={index === 0}>▲</button><button onClick={() => handleConditionMove(index, 'down')} disabled={index === conditions.length - 1}>▼</button></div><span>{condition.name}</span></div>
                    <div className="button-group"><button onClick={() => handleEditCondition(condition)} className="button small-button">Rediger</button><button onClick={() => handleDeleteCondition(condition.id)} className="button danger-button small-button">Slett</button></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
        <section id="section-testimonials" className="dashboard-section">
          <h2>Rediger Omtaler</h2>
          <div className="card">
            {editingTestimonial ? (
              <div className="article-editor">
                <h3>{editingTestimonial.id ? 'Rediger omtale' : 'Ny omtale'}</h3>
                <div className="form-group">
                  <label>Sitat / Omtale</label>
                  <textarea value={editingTestimonial.quote} onChange={e => handleTestimonialChange('quote', e.target.value)} rows="5"></textarea>
                </div>
                <div className="form-group">
                  <label>Avsender (Navn)</label>
                  <input type="text" value={editingTestimonial.author} onChange={e => handleTestimonialChange('author', e.target.value)} />
                </div>
                <div className="button-group">
                  <button onClick={handleSaveTestimonial} className="button">Lagre Omtale</button>
                  <button onClick={() => setEditingTestimonial(null)} className="button danger-button">Avbryt</button>
                </div>
              </div>
            ) : (
              <div className="article-list">
                <button onClick={handleNewTestimonial} className="button new-article-button">Legg til ny omtale</button>
                {testimonials.map((testimonial, index) => (
                  <div key={testimonial.id} className="admin-article-item">
                    <div className="category-title-group">
                      <div className="category-order-buttons">
                        <button onClick={() => handleTestimonialMove(index, 'up')} disabled={index === 0}>▲</button>
                        <button onClick={() => handleTestimonialMove(index, 'down')} disabled={index === testimonials.length - 1}>▼</button>
                      </div>
                      <span>"{testimonial.quote.substring(0, 50)}..." - {testimonial.author}</span>
                    </div>
                    <div className="button-group">
                      <button onClick={() => handleEditTestimonial(testimonial)} className="button small-button">Rediger</button>
                      <button onClick={() => handleDeleteTestimonial(testimonial.id)} className="button danger-button small-button">Slett</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default AdminDashboard;